import {lazy, Suspense} from 'react';
import {Navigate, Route, Routes, useParams} from 'react-router-dom';
import {Spin} from 'antd';
import {useSelector} from 'react-redux';
import UserDetails from './UserDetails';
import {canAccessTabForGrant} from './userTabsConfig';

const lazyNamed = (factory, exportName) =>
  lazy(() =>
    factory().then(module => ({
      default: module[exportName],
    })),
  );

const UserFeed = lazy(() => import('./tabs/feed/UserFeed'));
const UserInterests = lazy(() => import('./tabs/interest/UserInterests'));
const InvestmentDetails = lazy(() => import('./tabs/investment/Investment'));
const UserOpportunities = lazy(() =>
  import('./tabs/opportunities/UserOpportunities'),
);
const ProfileDetails = lazy(() =>
  import('./tabs/profileDetails/ProfileDetails'),
);
const ProfileUpdateHistory = lazy(() =>
  import('./tabs/profileUpdateHistory/ProfileUpdateHistory'),
);
const AccountActivity = lazy(() =>
  import('./tabs/accountActivity/AccountActivity'),
);
const PaymentAccounts = lazy(() =>
  import('./tabs/paymentAccounts/PaymentAccounts'),
);
const UserBookmarked = lazy(() => import('./tabs/userBookmark/UserBookmarked'));
const UserDetailsOverview = lazy(() => import('./tabs/overview/UserOverview'));
const UserPersona = lazy(() => import('./tabs/userPersona/UserPersona'));
const RiskProfile = lazy(() => import('./tabs/riskProfile/RiskProfile'));
const UsersSubscription = lazy(() => import('./tabs/subscription/index'));
const UpdateKyc = lazy(() => import('./updateKyc/UpdateKyc'));
const UserTransactions = lazy(() => import('./tabs/trades'));
const UpdateTransaction = lazy(() =>
  import('./tabs/transactions/UpdateTransaction'),
);
const NotificationPreference = lazyNamed(
  () => import('./tabs/notificationPreference'),
  'NotificationPreference',
);
const UserDocuments = lazy(() => import('./tabs/documents'));
const GrantSubscriptionAccess = lazy(() =>
  import('./tabs/subscription/price/GrantSubscriptionAccess'),
);
const UserSecurity = lazy(() => import('./tabs/userSecurity/UserSecurity'));
const UserWalletSettings = lazy(() =>
  import('./tabs/UserWalletSettings/UserWalletSettings'),
);
const RecordedUserSession = lazy(() =>
  import('./tabs/RecordedUserSession/RecordedUserSession'),
);
const SingleUserReferral = lazyNamed(
  () => import('./tabs/userReferral/UserReferral'),
  'SingleUserReferral',
);
const UserPortfolio = lazyNamed(
  () => import('./tabs/userPortfolio/userPortfolio'),
  'UserPortfolio',
);
const UserInteractions = lazyNamed(
  () => import('./tabs/Interactions/UserInteractions'),
  'UserInteractions',
);
const UserRecurringInvestment = lazyNamed(
  () => import('./tabs/recurringInvestments/UserRecurringInvestment'),
  'UserRecurringInvestment',
);
const UserLiens = lazyNamed(
  () => import('./tabs/userLiens/UserLiens'),
  'UserLiens',
);
const ClaimUser = lazyNamed(() => import('./claimUser/ClaimUser'), 'ClaimUser');

const RouteLoader = () => (
  <div style={{display: 'flex', justifyContent: 'center', padding: 50}}>
    <Spin size="large" />
  </div>
);

const RequireTabAccess = ({tabKey, children}) => {
  const {id} = useParams();
  const activeAdminTab = useSelector(
    state => state?.auth?.userData?.user?.activeAdminTab,
  );
  // The persisted auth slice can land a render after a hard reload; deciding
  // before it does would bounce a granted admin to the overview.
  if (!activeAdminTab) return <RouteLoader />;
  if (!canAccessTabForGrant(activeAdminTab, tabKey)) {
    return <Navigate to={`/dashboard/user/${id}/overview`} replace />;
  }
  return children;
};

const UserRoutes = () => {
  const renderRoute = Component => (
    <Suspense fallback={<RouteLoader />}>
      <Component />
    </Suspense>
  );

  return (
    <Routes>
      <Route path="/" element={<UserDetails />}>
        <Route index element={renderRoute(ProfileDetails)} />
        <Route path="/overview" element={renderRoute(UserDetailsOverview)} />
        <Route
          path="/persona"
          element={
            <RequireTabAccess tabKey="persona">
              {renderRoute(UserPersona)}
            </RequireTabAccess>
          }
        />
        <Route path="/risk-profile" element={renderRoute(RiskProfile)} />
        <Route
          path="/banks"
          element={<Navigate to="../payment-accounts" replace />}
        />
        <Route path="/investment" element={renderRoute(InvestmentDetails)} />
        <Route path="/interests" element={renderRoute(UserInterests)} />
        <Route path="/bookmark" element={renderRoute(UserBookmarked)} />
        <Route path="/saved" element={renderRoute(UserOpportunities)} />
        <Route path="/kyc-status" element={renderRoute(UpdateKyc)} />
        <Route
          path="/account-activity"
          element={
            <RequireTabAccess tabKey="account-activity">
              {renderRoute(AccountActivity)}
            </RequireTabAccess>
          }
        />
        <Route
          path="/payment-accounts"
          element={
            <RequireTabAccess tabKey="payment-accounts">
              {renderRoute(PaymentAccounts)}
            </RequireTabAccess>
          }
        />
        <Route
          path="/profile-history"
          element={renderRoute(ProfileUpdateHistory)}
        />
        <Route path="/portfolio" element={renderRoute(UserPortfolio)} />
        <Route path="/feed" element={renderRoute(UserFeed)} />
        <Route path="/subscription" element={renderRoute(UsersSubscription)} />
        <Route
          path="/subscription-price/:accessType"
          element={renderRoute(GrantSubscriptionAccess)}
        />
        <Route path="/interactions" element={renderRoute(UserInteractions)} />
        <Route path="/transactions" element={renderRoute(UserTransactions)} />
        <Route path="/documents" element={renderRoute(UserDocuments)} />
        <Route
          path="/notification-preferences"
          element={renderRoute(NotificationPreference)}
        />
        <Route path="/claim-user" element={renderRoute(ClaimUser)} />
        <Route path="/liens" element={renderRoute(UserLiens)} />
        <Route
          path="/recurring-investment"
          element={renderRoute(UserRecurringInvestment)}
        />
        <Route
          path="/update-transaction/:tradeId"
          element={renderRoute(UpdateTransaction)}
        />
        <Route path="/user-security" element={renderRoute(UserSecurity)} />
        <Route path="/sessions" element={renderRoute(RecordedUserSession)} />
        <Route path="/referral" element={renderRoute(SingleUserReferral)} />
        <Route
          path="/wallet-settings"
          element={renderRoute(UserWalletSettings)}
        />
      </Route>
    </Routes>
  );
};

export default UserRoutes;
